/**
 * Reusable field group for task assignment (role/clinic/staff).
 *
 * Renders a team/member toggle with conditional role, clinic, and staff
 * fields. All fields participate in the parent form's validation and state.
 *
 * Used in both task creation forms and the task reassignment form.
 */

import { useStore } from '@tanstack/react-form'
import { useState } from 'react'
import { z } from 'zod'

import {
  CLINIC_STAFF_ROLES,
  type ClinicId,
  clinicIdSchema,
  STAFF_ROLES,
  type StaffRole,
  staffRoleSchema,
} from '@snootsvet/shared'

import { ClinicSearchWithDefault } from '@/frontend/components/clinic-search'
import { withFieldGroup } from '@/frontend/components/form'
import { FieldWrapper } from '@/frontend/components/form/fields'
import { StaffSearch } from '@/frontend/components/staff-search'
import { Button } from '@/frontend/components/ui/button'

/** Zod shape for assignment fields. Spread into form-level schemas. */
export const taskAssignmentShape = {
  assigneeRole: staffRoleSchema.nullable(),
  assigneeClinicId: clinicIdSchema.nullable(),
  assigneeStaffId: z.string().nullable(),
}

/** Returns true if at least one of role or staff member is assigned. */
export function hasAssignee(v: { assigneeRole: unknown; assigneeStaffId: unknown }) {
  return !!v.assigneeRole || !!v.assigneeStaffId
}

/** Returns true unless a clinic-requiring role (vet/vca/nurse) is selected without a clinic. */
export function hasClinicWhenRequired(v: {
  assigneeRole: StaffRole | null
  assigneeClinicId: ClinicId | null
}) {
  return !v.assigneeRole || !CLINIC_STAFF_ROLES.has(v.assigneeRole) || !!v.assigneeClinicId
}

export interface TaskAssignmentFields {
  assigneeRole: StaffRole | null
  assigneeClinicId: ClinicId | null
  assigneeStaffId: string | null
}

export const TASK_ASSIGNMENT_DEFAULTS: TaskAssignmentFields = {
  assigneeRole: null,
  assigneeClinicId: null,
  assigneeStaffId: null,
}

export const TaskAssignmentFieldGroup = withFieldGroup({
  defaultValues: TASK_ASSIGNMENT_DEFAULTS,
  render: ({ group }) => {
    const assigneeRole = useStore(group.store, (s) => s.values.assigneeRole)
    const clinicRequired = !!assigneeRole && CLINIC_STAFF_ROLES.has(assigneeRole)

    const [assignMode, setAssignMode] = useState<'team' | 'member'>('team')

    return (
      <div className="flex flex-col gap-2">
        <span className="font-medium">Assign to</span>

        <div className="flex gap-2">
          {(['team', 'member'] as const).map((mode) => (
            <Button
              key={mode}
              type="button"
              variant={assignMode === mode ? 'default' : 'outline'}
              className="flex-1"
              onClick={() => {
                setAssignMode(mode)
                if (mode === 'team') {
                  group.setFieldValue('assigneeStaffId', null)
                } else {
                  group.setFieldValue('assigneeRole', null)
                  group.setFieldValue('assigneeClinicId', null)
                }
              }}
            >
              {mode === 'team' ? 'Team' : 'Staff member'}
            </Button>
          ))}
        </div>

        {assignMode === 'team' && (
          <>
            <group.AppField
              name="assigneeRole"
              listeners={{
                onChange: ({ fieldApi }) => {
                  // The clinic selector only renders for clinic-requiring
                  // roles, so drop any stale selection when switching away.
                  if (!fieldApi.state.value || !CLINIC_STAFF_ROLES.has(fieldApi.state.value)) {
                    group.setFieldValue('assigneeClinicId', null)
                  }
                },
              }}
            >
              {(field) => (
                <field.SelectField
                  label="Team"
                  placeholder="Select a team"
                  items={STAFF_ROLES}
                  required
                  value={field.state.value}
                />
              )}
            </group.AppField>

            {clinicRequired && (
              <group.Field name="assigneeClinicId">
                {(field) => (
                  <FieldWrapper
                    field={field}
                    label="Clinic"
                    required
                    render={({ id, disabled }) => (
                      <ClinicSearchWithDefault
                        id={id}
                        value={field.state.value}
                        onChange={field.handleChange}
                        onBlur={field.handleBlur}
                        disabled={disabled}
                        allowClear={false}
                      />
                    )}
                  />
                )}
              </group.Field>
            )}
          </>
        )}

        {assignMode === 'member' && (
          <group.Field name="assigneeStaffId">
            {(field) => (
              <FieldWrapper
                field={field}
                label="Staff member"
                render={({ id, disabled }) => (
                  <StaffSearch
                    id={id}
                    value={field.state.value}
                    onChange={(staff) => field.handleChange(staff?.id ?? null)}
                    onBlur={field.handleBlur}
                    disabled={disabled}
                  />
                )}
              />
            )}
          </group.Field>
        )}
      </div>
    )
  },
})
